import type { CSSProperties, ReactNode } from 'react'

export type BadgeTone =
  | 'emerald'
  | 'gold'
  | 'turquoise'
  | 'terracotta'
  | 'indigo'
  | 'plum'
  | 'danger'
  | 'warning'
  | 'info'

export interface BadgeProps {
  tone?: BadgeTone
  /** Solid fill (used over colored banners). */
  solid?: boolean
  iconLeft?: ReactNode
  children?: ReactNode
  style?: CSSProperties
}

const TONES: Record<
  BadgeTone,
  { color: string; tint: string; solidText?: string }
> = {
  emerald: { color: 'var(--emerald-600)', tint: 'var(--emerald-100)' },
  gold: {
    color: 'var(--gold-600)',
    tint: 'var(--gold-100)',
    solidText: 'var(--ink-0)',
  },
  turquoise: { color: 'var(--turquoise)', tint: 'var(--turquoise-tint)' },
  terracotta: {
    color: 'var(--terracotta)',
    tint: 'var(--terracotta-tint)',
    solidText: 'var(--ink-0)',
  },
  indigo: { color: 'var(--indigo)', tint: 'var(--indigo-tint)' },
  plum: { color: 'var(--plum)', tint: 'var(--plum-tint)' },
  danger: { color: 'var(--danger)', tint: 'var(--danger-tint)' },
  warning: { color: 'var(--warning)', tint: 'var(--warning-tint)' },
  info: { color: 'var(--info)', tint: 'var(--info-tint)' },
}

export function Badge({
  tone = 'emerald',
  solid,
  iconLeft,
  children,
  style,
}: BadgeProps) {
  const t = TONES[tone]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontFamily: 'var(--font-body)',
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1,
        padding: '5px 10px',
        borderRadius: 'var(--radius-pill)',
        background: solid ? t.color : t.tint,
        color: solid ? (t.solidText ?? '#fff') : t.color,
        border: solid ? 'none' : `1px solid ${t.color}33`,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {iconLeft}
      {children}
    </span>
  )
}

export default Badge
